import React, { useRef, useState } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import type { ValidationPhoto } from '../../types/barcodeValidation';

interface Props {
  photos: ValidationPhoto[];
  onAddPhoto: (file: File) => void;
  onRemovePhoto: (id: string) => void;
}

export function ValidationPhotoManager({ photos, onAddPhoto, onRemovePhoto }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [modalPhoto, setModalPhoto] = useState<ValidationPhoto | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      onAddPhoto(files[i]);
    }
    e.target.value = '';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-teal-600" />
          Fotos da Etiqueta
        </h2>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
        >
          <Upload className="w-4 h-4" />
          Adicionar Foto
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {photos.length === 0 ? (
        <p className="text-sm text-gray-400 italic">Nenhuma foto adicionada</p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {photos.map((photo) => (
            <div key={photo.id} className="relative group">
              <img
                src={photo.dataUrl}
                alt={photo.name}
                className="w-24 h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:border-teal-400 transition-colors"
                onClick={() => setModalPhoto(photo)}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemovePhoto(photo.id);
                }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
              <p className="text-xs text-gray-500 mt-1 truncate w-24">{photo.name}</p>
            </div>
          ))}
        </div>
      )}

      {/* Modal fullsize */}
      {modalPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setModalPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setModalPhoto(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white text-gray-800 rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 z-10"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={modalPhoto.dataUrl}
              alt={modalPhoto.name}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-white text-center mt-2 text-sm">{modalPhoto.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}
